import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  MarketplacePayment,
  MarketplacePaymentRevision,
  Dispute,
  AdjustPaymentRequest,
  ApprovePaymentRequest,
  DeclinePaymentRequest,
  EarlyReleaseRequest,
  ResolveDisputeRequest,
  PendingApprovalFilters,
  AllPaymentsFilters,
  DisputesFilters,
} from '../models/marketplace-payment.model';

/**
 * MarketplacePaymentsAdminService
 *
 * Spec: /agent-os/specs/2026-06-10-deferred-capture-admin-approval/spec.md (§ 5.3.1)
 *
 * Wraps the new Hub admin endpoints introduced 2026-06-11:
 *   - GET    /admin/payments/pending-approval
 *   - POST   /admin/payments/pending-approval/:id/approve
 *   - POST   /admin/payments/pending-approval/:id/adjust
 *   - POST   /admin/payments/pending-approval/:id/decline
 *   - POST   /admin/payments/payouts/:id/release  (early-release w/ reason)
 *   - GET    /admin/payments/:id
 *   - GET    /admin/payments/:id/revisions
 *   - GET    /admin/disputes
 *   - GET    /admin/disputes/:id
 *   - POST   /admin/disputes/:id/resolve
 *
 * Conventions follow `DroneService`: HttpClient injected, environment.apiUrl
 * base URL, RxJS Observables, response unwrapping via `pipe(map(...))`.
 *
 * Auth tokens come from the global Firebase auth interceptor — no
 * per-call header wiring needed here.
 */
@Injectable({ providedIn: 'root' })
export class MarketplacePaymentsAdminService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  // ─── Pending approval queue ────────────────────────────────────────

  listPendingApproval(filters: PendingApprovalFilters = {}): Observable<MarketplacePayment[]> {
    let params = new HttpParams();
    if (filters.farmerOrgId) params = params.set('farmerOrgId', filters.farmerOrgId);
    if (filters.pilotOrgId) params = params.set('pilotOrgId', filters.pilotOrgId);
    if (filters.createdAtFrom) params = params.set('createdAtFrom', filters.createdAtFrom);
    if (filters.createdAtTo) params = params.set('createdAtTo', filters.createdAtTo);
    if (filters.minTotalCharge != null) params = params.set('minTotalCharge', String(filters.minTotalCharge));
    if (filters.maxTotalCharge != null) params = params.set('maxTotalCharge', String(filters.maxTotalCharge));
    if (filters.limit != null) params = params.set('limit', String(filters.limit));
    if (filters.offset != null) params = params.set('offset', String(filters.offset));

    return this.http
      .get<{ success: boolean; count: number; payments: MarketplacePayment[] }>(
        `${this.apiUrl}/admin/payments/pending-approval`,
        { params },
      )
      .pipe(
        map((r) => {
          // A `{ success: false }` body on a 200 would otherwise unwrap
          // to an empty list and read as "no payments" — mask the
          // backend error. Throw so the component's error handler runs.
          if (!r?.success) throw new Error('Failed to load the approval queue.');
          return r.payments ?? [];
        }),
      );
  }

  /**
   * Backs the "All Payments" admin tab. Distinct from
   * `listPendingApproval` — this is a search/diagnostic view spanning
   * any lifecycle status, while pending-approval is the actionable
   * queue. Statuses pass as a comma-separated query param. Empty /
   * undefined statuses → all rows. (2026-06-11)
   */
  listAllPayments(filters: AllPaymentsFilters = {}): Observable<MarketplacePayment[]> {
    let params = new HttpParams();
    if (filters.statuses && filters.statuses.length > 0) {
      params = params.set('status', filters.statuses.join(','));
    }
    if (filters.farmerOrgId) params = params.set('farmerOrgId', filters.farmerOrgId);
    if (filters.pilotOrgId) params = params.set('pilotOrgId', filters.pilotOrgId);
    if (filters.createdAtFrom) params = params.set('createdAtFrom', filters.createdAtFrom);
    if (filters.createdAtTo) params = params.set('createdAtTo', filters.createdAtTo);
    if (filters.minTotalCharge != null) params = params.set('minTotalCharge', String(filters.minTotalCharge));
    if (filters.maxTotalCharge != null) params = params.set('maxTotalCharge', String(filters.maxTotalCharge));
    if (filters.limit != null) params = params.set('limit', String(filters.limit));
    if (filters.offset != null) params = params.set('offset', String(filters.offset));

    return this.http
      .get<{ success: boolean; count: number; payments: MarketplacePayment[] }>(
        `${this.apiUrl}/admin/payments`,
        { params },
      )
      .pipe(
        map((r) => {
          if (!r?.success) throw new Error('Failed to load payments.');
          return r.payments ?? [];
        }),
      );
  }

  // These endpoints return a discriminated-union `outcome` object. Note
  // we guard on `outcome` PRESENCE, NOT on `success`: a successful HTTP
  // 200 can legitimately carry `success: false` (e.g. the release
  // endpoint maps a `skipped` outcome to 200 + success:false), and the
  // component branches on `outcome.outcome` to render those states. The
  // genuine error paths (failed_*/in_flight) come back as 4xx/5xx, which
  // HttpClient throws on → the component's error handler. Throwing here
  // when the payload is missing catches a malformed `{ success:false }`
  // body without swallowing the valid skip/fail outcomes.

  approvePayment(paymentId: string, body: ApprovePaymentRequest = {}): Observable<any> {
    return this.http
      .post<{ success: boolean; outcome: any }>(
        `${this.apiUrl}/admin/payments/pending-approval/${paymentId}/approve`,
        body,
      )
      .pipe(map((r) => {
        if (!r?.outcome) throw new Error('Approve request returned no outcome.');
        return r.outcome;
      }));
  }

  adjustPayment(paymentId: string, body: AdjustPaymentRequest): Observable<any> {
    return this.http
      .post<{ success: boolean; outcome: any }>(
        `${this.apiUrl}/admin/payments/pending-approval/${paymentId}/adjust`,
        body,
      )
      .pipe(map((r) => {
        if (!r?.outcome) throw new Error('Adjust request returned no outcome.');
        return r.outcome;
      }));
  }

  declinePayment(paymentId: string, body: DeclinePaymentRequest): Observable<any> {
    return this.http
      .post<{ success: boolean; outcome: any }>(
        `${this.apiUrl}/admin/payments/pending-approval/${paymentId}/decline`,
        body,
      )
      .pipe(map((r) => {
        if (!r?.outcome) throw new Error('Decline request returned no outcome.');
        return r.outcome;
      }));
  }

  // ─── Early payout release (existing endpoint, now requires reason) ─

  releasePayoutEarly(paymentId: string, reason: string): Observable<any> {
    const body: EarlyReleaseRequest = { ignoreWindow: true, reason };
    return this.http
      .post<{ success: boolean; outcome: any }>(
        `${this.apiUrl}/admin/payments/payouts/${paymentId}/release`,
        body,
      )
      .pipe(map((r) => {
        if (!r?.outcome) throw new Error('Release request returned no outcome.');
        return r.outcome;
      }));
  }

  // ─── Diagnostic + revision timeline ────────────────────────────────

  getPayment(paymentId: string): Observable<MarketplacePayment> {
    return this.http
      .get<{ success: boolean; payment: MarketplacePayment }>(
        `${this.apiUrl}/admin/payments/${paymentId}`,
      )
      .pipe(
        map((r) => {
          // Guard a `{ success: false }`/missing-payload 200 so the
          // detail view doesn't try to render `undefined`.
          if (!r?.success || !r.payment) throw new Error('Payment not found.');
          return r.payment;
        }),
      );
  }

  getRevisions(paymentId: string): Observable<MarketplacePaymentRevision[]> {
    return this.http
      .get<{ success: boolean; count: number; revisions: MarketplacePaymentRevision[] }>(
        `${this.apiUrl}/admin/payments/${paymentId}/revisions`,
      )
      .pipe(
        map((r) => {
          if (!r?.success) throw new Error('Failed to load the revision history.');
          return r.revisions ?? [];
        }),
      );
  }

  // ─── Disputes ──────────────────────────────────────────────────────

  listDisputes(filters: DisputesFilters = {}): Observable<Dispute[]> {
    let params = new HttpParams();
    if (filters.status) params = params.set('status', filters.status);
    if (filters.reasonCode) params = params.set('reasonCode', filters.reasonCode);
    if (filters.createdAtFrom) params = params.set('createdAtFrom', filters.createdAtFrom);
    if (filters.createdAtTo) params = params.set('createdAtTo', filters.createdAtTo);
    if (filters.limit != null) params = params.set('limit', String(filters.limit));
    if (filters.offset != null) params = params.set('offset', String(filters.offset));

    return this.http
      .get<{ success: boolean; count: number; disputes: Dispute[] }>(
        `${this.apiUrl}/admin/disputes`,
        { params },
      )
      .pipe(
        map((r) => {
          if (!r?.success) throw new Error('Failed to load disputes.');
          return r.disputes ?? [];
        }),
      );
  }

  getDispute(disputeId: string): Observable<Dispute> {
    return this.http
      .get<{ success: boolean; dispute: Dispute }>(
        `${this.apiUrl}/admin/disputes/${disputeId}`,
      )
      .pipe(
        map((r) => {
          if (!r?.success || !r.dispute) throw new Error('Dispute not found.');
          return r.dispute;
        }),
      );
  }

  resolveDispute(disputeId: string, body: ResolveDisputeRequest): Observable<Dispute> {
    return this.http
      .post<{ success: boolean; dispute: Dispute; scheduleRelease: any }>(
        `${this.apiUrl}/admin/disputes/${disputeId}/resolve`,
        body,
      )
      .pipe(
        map((r) => {
          // Unlike the outcome endpoints, resolve returns the dispute
          // row on success (200) and a 4xx on any DisputeError — so a
          // 200 here must carry success + the row.
          if (!r?.success || !r.dispute) throw new Error('Resolve request returned no dispute.');
          return r.dispute;
        }),
      );
  }
}
